import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Building,
    ChevronDown,
    CircleMinus,
    CirclePlus,
    Eye,
    GripVertical,
    ListFilter,
    Loader2,
    X,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Input } from '@apps/web/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    type Column,
    type ColumnFiltersState,
    type RowData,
    type Table as TableType,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';

type FacetConfig = {
    /** Column id to filter (must exist on the table) */
    readonly columnId: string;
    /** Stable analytics id for this facet (falls back to columnId). */
    readonly analyticsId?: string;
    /** Label for the menu */
    readonly title: string;
    /** Icon for the menu item */
    readonly icon?: React.ComponentType<{ className?: string }>;
    /** Optional formatter for values (e.g., title case for office names) */
    readonly valueFormatter?: (val: unknown) => string;
    /** Optional custom renderer for values in the filter dropdown (e.g., badges) */
    readonly renderValue?: (val: unknown) => React.ReactNode;
    /** Max options to show (to avoid huge menus) */
    readonly limit?: number;
    /** Hint for whether the column value is an array or single value */
    readonly valueKind?: 'array' | 'single';
    /** Default include operator when multiple values are selected */
    readonly defaultIncludeMode?: 'any' | 'all';
    /** Default exclude operator when multiple values are selected */
    readonly defaultExcludeMode?: 'any' | 'all';
    /** Predefined options to always show (useful for server-side filtering) */
    readonly predefinedOptions?: ReadonlyArray<unknown>;
};

export type CustomFilterConfig = {
    /** Unique identifier for the filter */
    readonly id: string;
    /** Label for the menu */
    readonly title: string;
    /** Icon for the menu item */
    readonly icon?: React.ComponentType<{ className?: string }>;
    /** Operator label rendered on the chip (defaults to "is") */
    readonly operatorLabel?: React.ReactNode;
    /** Number of active selections (for badge count) */
    readonly selectedCount: number;
    /** Content to render in the submenu */
    readonly children: React.ReactNode;
    /** Labels for the active filter chip (e.g., selected coordinator names) */
    readonly activeFilterLabels?: Array<string>;
    /** Callback to clear the filter */
    readonly onClear?: () => void;
    /** Render function for the value selection (receives open state and setter) */
    readonly renderValueSelection?: (props: {
        open: boolean;
        onOpenChange: (open: boolean) => void;
    }) => React.ReactNode;
    /** Callback when the filter dropdown opens (for capturing initial state) */
    readonly onOpen?: () => void;
};

export type ToolbarFilterChangedEvent = {
    readonly filterType: string;
    readonly action:
        | 'add'
        | 'remove'
        | 'include'
        | 'exclude'
        | 'clear'
        | 'change_operator';
    readonly value?: string;
    readonly filterMode?: 'include' | 'exclude';
    readonly operator?:
        | 'is'
        | 'is_not'
        | 'includes_any'
        | 'includes_all'
        | 'excludes_any'
        | 'excludes_all';
};

export type ToolbarFiltersClearedEvent = {
    readonly hadSearch: boolean;
    readonly clearedFacetCount: number;
    readonly clearedCustomFilterCount: number;
};

type FacetSelectionState = {
    readonly include: Array<unknown>;
    readonly exclude: Array<unknown>;
    readonly includeMode?: 'any' | 'all';
    readonly excludeMode?: 'any' | 'all';
};

type FacetOption = {
    readonly value: unknown;
    readonly label: string;
    readonly key: string;
    readonly serialized: string;
    readonly isIncluded: boolean;
    readonly isExcluded: boolean;
};

type FacetContext = {
    readonly facet: FacetConfig;
    readonly selection: FacetSelectionState;
    readonly formatValue: (val: unknown) => string;
    readonly isArray: boolean;
    readonly options: Array<FacetOption>;
};

const EMPTY_COLUMN_FILTERS: ColumnFiltersState = [];

const serialize = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const normalizeFacetSelection = (value: unknown): FacetSelectionState => {
    if (value === null || value === undefined) {
        return { include: [], exclude: [] };
    }
    if (Array.isArray(value)) {
        return { include: value, exclude: [] };
    }
    if (value && typeof value === 'object') {
        const { include, exclude, includeMode, excludeMode } = value as {
            include?: Array<unknown>;
            exclude?: Array<unknown>;
            includeMode?: 'any' | 'all';
            excludeMode?: 'any' | 'all';
        };
        return {
            include: Array.isArray(include) ? include : [],
            exclude: Array.isArray(exclude) ? exclude : [],
            includeMode:
                includeMode === 'all' || includeMode === 'any'
                    ? includeMode
                    : undefined,
            excludeMode:
                excludeMode === 'all' || excludeMode === 'any'
                    ? excludeMode
                    : undefined,
        };
    }
    return { include: [value], exclude: [] };
};

const buildFacetEntries = <TData extends RowData>(
    table: TableType<TData>,
    columnId: string,
    predefinedOptions?: ReadonlyArray<unknown>,
): Array<[unknown, number]> => {
    const column = table.getColumn(columnId);
    if (!column) {
        return [];
    }

    // Get entries from column facets or rows
    const rawEntries = Array.from(
        column.getFacetedUniqueValues().entries(),
    ) as Array<[unknown, number]>;
    const entries =
        rawEntries.length > 0
            ? rawEntries
            : Array.from(
                  table
                      .getPreFilteredRowModel()
                      .flatRows.reduce((counts, row) => {
                          const value = row.getValue(columnId);
                          const items = Array.isArray(value) ? value : [value];
                          items.forEach((item) =>
                              counts.set(item, (counts.get(item) ?? 0) + 1),
                          );
                          return counts;
                      }, new Map<unknown, number>()),
              );

    // Flatten array values
    const counts = new Map<unknown, number>();
    entries.forEach(([key, count]) => {
        const items = Array.isArray(key) ? key : [key];
        items.forEach((item) =>
            counts.set(item, (counts.get(item) ?? 0) + count),
        );
    });

    // If predefined options are provided, use them as the base and merge counts
    if (predefinedOptions) {
        const result: Array<[unknown, number]> = [];
        const seen = new Set<string>();

        for (const option of predefinedOptions) {
            const key = serialize(option);
            if (!seen.has(key)) {
                seen.add(key);
                result.push([option, counts.get(option) ?? 0]);
            }
        }

        return result;
    }

    return Array.from(counts.entries());
};

const applyFacetLimit = (
    entries: Array<[unknown, number]>,
    selection: FacetSelectionState,
    limit?: number,
) => {
    const limitedEntries =
        limit && entries.length > limit
            ? entries.slice(0, limit)
            : [...entries];
    const seen = new Set(limitedEntries.map(([value]) => serialize(value)));
    let hasMissingSelectedValue = false;

    // Always include selected values even if they're outside the limit
    [...selection.include, ...selection.exclude].forEach((value) => {
        const key = serialize(value);
        if (!seen.has(key)) {
            limitedEntries.push([value, 0]);
            seen.add(key);
            hasMissingSelectedValue = true;
        }
    });

    if (!hasMissingSelectedValue && limitedEntries.length === entries.length) {
        return entries;
    }

    return limitedEntries;
};

const buildFacetContext = <TData extends RowData>(
    table: TableType<TData>,
    facet: FacetConfig,
    columnFilters: ColumnFiltersState,
): FacetContext | null => {
    const column = table.getColumn(facet.columnId);
    if (!column) {
        return null;
    }

    const filterValue =
        columnFilters.find(
            (filter: ColumnFiltersState[number]) =>
                filter.id === facet.columnId,
        )?.value ?? column.getFilterValue();
    const selection = normalizeFacetSelection(filterValue);
    const formatValue =
        facet.valueFormatter ?? ((val: unknown) => String(val ?? '—'));

    const rawEntries = buildFacetEntries(
        table,
        facet.columnId,
        facet.predefinedOptions,
    )
        .filter(
            ([value]) =>
                value !== null &&
                value !== undefined &&
                String(value).trim() !== '',
        )
        .sort((a, b) => String(a[0] ?? '').localeCompare(String(b[0] ?? '')));

    const entries = applyFacetLimit(rawEntries, selection, facet.limit);
    const selectedValues = new Set(
        [...selection.include, ...selection.exclude].map(serialize),
    );

    const options = entries.map(([value]) => {
        const serialized = serialize(value);
        return {
            value,
            label: formatValue(value),
            key: `${facet.columnId}:${serialized}`,
            serialized,
            isIncluded:
                selectedValues.has(serialized) &&
                selection.include.some((v) => serialize(v) === serialized),
            isExcluded:
                selectedValues.has(serialized) &&
                selection.exclude.some((v) => serialize(v) === serialized),
        };
    });

    return {
        facet,
        selection,
        formatValue,
        isArray: facet.valueKind === 'array',
        options,
    };
};

const buildActiveFacetEntries = (
    contexts: Array<FacetContext>,
): Array<ActiveFacetEntry> => {
    const entries: Array<ActiveFacetEntry> = [];

    contexts.forEach(({ facet, selection, formatValue, isArray, options }) => {
        const labelMap = new Map(
            options.map((opt) => [opt.serialized, opt.label]),
        );

        const createEntry = (
            type: 'include' | 'exclude',
            values: Array<unknown>,
        ) => {
            if (values.length === 0) {
                return;
            }

            const serializedValues = values.map(serialize);
            const labels = serializedValues.map(
                (s, i) => labelMap.get(s) ?? formatValue(values[i]),
            );
            const mode =
                type === 'include'
                    ? selection.includeMode
                    : selection.excludeMode;

            entries.push({
                key: `${facet.columnId}:${type}`,
                columnId: facet.columnId,
                analyticsId: facet.analyticsId ?? facet.columnId,
                facetTitle: facet.title,
                values,
                serializedValues,
                labels,
                type,
                includeMode:
                    type === 'include'
                        ? (mode ??
                          (values.length > 1
                              ? (facet.defaultIncludeMode ?? 'any')
                              : undefined))
                        : undefined,
                excludeMode:
                    type === 'exclude'
                        ? (mode ??
                          (values.length > 1
                              ? (facet.defaultExcludeMode ?? 'any')
                              : undefined))
                        : undefined,
                isArray,
                options,
            });
        };

        createEntry('include', selection.include);
        createEntry('exclude', selection.exclude);
    });

    return entries;
};

const dedupe = (values: Array<unknown>): Array<unknown> => {
    const seen = new Set<string>();
    return values.filter((v) => {
        const key = serialize(v);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

const applyFacetSelection = <TData extends RowData>(
    table: TableType<TData>,
    columnId: string,
    updater: (current: FacetSelectionState) => FacetSelectionState,
    defaults?: {
        includeMode?: 'any' | 'all';
        excludeMode?: 'any' | 'all';
    },
) => {
    const column = table.getColumn(columnId);
    if (!column) {
        return;
    }

    const current = normalizeFacetSelection(column.getFilterValue());
    const next = updater(current);
    const include = dedupe(next.include);
    const exclude = dedupe(next.exclude);

    // Clear filter when no selections
    if (include.length === 0 && exclude.length === 0) {
        column.setFilterValue(undefined);
        return;
    }

    const includeMode =
        include.length === 0
            ? undefined
            : next.includeMode === 'all' || next.includeMode === 'any'
              ? next.includeMode
              : include.length > 1
                ? (defaults?.includeMode ?? 'any')
                : undefined;

    const excludeMode =
        exclude.length === 0
            ? undefined
            : next.excludeMode === 'all' || next.excludeMode === 'any'
              ? next.excludeMode
              : exclude.length > 1
                ? (defaults?.excludeMode ?? 'any')
                : undefined;

    const filterValue: {
        include: Array<unknown>;
        exclude: Array<unknown>;
        includeMode?: 'any' | 'all';
        excludeMode?: 'any' | 'all';
    } = { include, exclude };

    if (includeMode) {
        filterValue.includeMode = includeMode;
    }
    if (excludeMode) {
        filterValue.excludeMode = excludeMode;
    }

    column.setFilterValue(filterValue);
};

type ActiveFacetEntry = {
    readonly key: string;
    readonly columnId: string;
    readonly analyticsId: string;
    readonly facetTitle: string;
    readonly values: Array<unknown>;
    readonly serializedValues: Array<string>;
    readonly labels: Array<string>;
    readonly type: 'include' | 'exclude';
    readonly includeMode?: 'any' | 'all';
    readonly excludeMode?: 'any' | 'all';
    readonly isArray: boolean;
    readonly options: Array<FacetOption>;
};

function CustomFilterChipInner({ filter }: { filter: CustomFilterConfig }) {
    const [open, setOpen] = React.useState(false);
    const valueLabel = filter.activeFilterLabels?.join(', ') || '—';
    const operatorLabel = filter.operatorLabel ?? 'is';

    return (
        <div className="border-border bg-muted/60 flex items-center gap-1 rounded-full border py-1 pr-1 pl-3">
            <span className="text-xs font-medium">{filter.title}</span>
            {operatorLabel ? (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 items-center gap-1 rounded-full border-blue-500 px-3 text-xs text-blue-600 hover:bg-blue-50"
                >
                    <span className="truncate">{operatorLabel}</span>
                </Button>
            ) : null}
            {filter.renderValueSelection ? (
                filter.renderValueSelection({
                    open,
                    onOpenChange: setOpen,
                })
            ) : (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={true}
                    className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                >
                    {valueLabel}
                </Button>
            )}
            {filter.onClear && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full"
                    onClick={filter.onClear}
                    aria-label={`Clear ${filter.title} filter`}
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
}

function getColumnDisplayName<TData extends RowData>(
    column: Column<TData, unknown>,
): string {
    const meta = column.columnDef.meta as { displayName?: string } | undefined;
    const label = column.columnDef.header as React.ReactNode;
    return (
        meta?.displayName ||
        (typeof label === 'string'
            ? label
            : (column.id.charAt(0).toUpperCase() + column.id.slice(1)).replace(
                  /_/g,
                  ' ',
              ))
    );
}

// TanStack appends any leaf column absent from columnOrder at the END, so the
// non-reorderable pinned ids must be placed explicitly at the front.
export function computeReorderedColumnIds(
    pinnedIds: ReadonlyArray<string>,
    reorderableIds: ReadonlyArray<string>,
    activeId: string,
    overId: string,
): Array<string> {
    const oldIndex = reorderableIds.indexOf(activeId);
    const newIndex = reorderableIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) {
        return [...pinnedIds, ...reorderableIds];
    }
    return [
        ...pinnedIds,
        ...arrayMove([...reorderableIds], oldIndex, newIndex),
    ];
}

function SortableColumnRow({
    id,
    label,
    checked,
    onCheckedChange,
}: {
    readonly id: string;
    readonly label: string;
    readonly checked: boolean;
    readonly onCheckedChange: (checked: boolean) => void;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-center gap-2 rounded-sm px-1 py-1.5',
                isDragging && 'bg-muted',
            )}
        >
            <button
                type="button"
                aria-label={`Reorder ${label}`}
                className="text-muted-foreground hover:text-foreground flex h-6 w-5 shrink-0 cursor-grab items-center justify-center"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <Checkbox
                id={`column-toggle-${id}`}
                checked={checked}
                onCheckedChange={(value) => onCheckedChange(value === true)}
            />
            <label
                htmlFor={`column-toggle-${id}`}
                className="flex-1 cursor-pointer text-sm capitalize"
            >
                {label}
            </label>
        </div>
    );
}

function ColumnReorderPopover<TData extends RowData>({
    table,
    buttonClassName,
}: {
    readonly table: TableType<TData>;
    readonly buttonClassName?: string;
}) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );
    const leafColumns = table.getAllLeafColumns();
    const leafById = new Map(leafColumns.map((column) => [column.id, column]));
    const listedIds = table.state.columnOrder.filter((id) => leafById.has(id));
    const listedSet = new Set(listedIds);
    const effectiveOrder = [
        ...listedIds,
        ...leafColumns
            .map((column) => column.id)
            .filter((id) => !listedSet.has(id)),
    ];
    const pinnedIds = effectiveOrder.filter(
        (id) => leafById.get(id)?.getCanHide() === false,
    );
    const reorderableIds = effectiveOrder.filter(
        (id) => leafById.get(id)?.getCanHide() !== false,
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        table.setColumnOrder(
            computeReorderedColumnIds(
                pinnedIds,
                reorderableIds,
                String(active.id),
                String(over.id),
            ),
        );
    };

    return (
        <Popover>
            <PopoverTrigger asChild={true}>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="Toggle columns"
                    className={buttonClassName}
                >
                    <Eye className="h-4 w-4 md:mr-2" />
                    <span className="hidden md:inline">Columns</span>
                    <ChevronDown className="ml-1 hidden h-4 w-4 md:inline-block" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="flex max-h-[var(--radix-popover-content-available-height)] w-56 flex-col p-1"
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={reorderableIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                            {reorderableIds.map((id) => {
                                const column = leafById.get(id);
                                if (!column) {
                                    return null;
                                }
                                return (
                                    <SortableColumnRow
                                        key={id}
                                        id={id}
                                        label={getColumnDisplayName(column)}
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(checked) =>
                                            column.toggleVisibility(checked)
                                        }
                                    />
                                );
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
                <div className="mt-1 border-t pt-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => table.setColumnOrder([])}
                    >
                        Reset order
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export function DataTableToolbar<TData extends RowData>({
    table,
    globalFilterPlaceholder = 'Search…',
    facetedFilters = [],
    customFilters = [],
    hideGlobalSearch = false,
    extraButtons,
    globalFilterValue,
    onGlobalFilterChange,
    serverSideGlobalFilter = false,
    isLoading = false,
    mobileCompact = false,
    hideColumnsButton = false,
    enableColumnReorder = false,
    onFilterChanged,
    onFiltersCleared,
}: {
    table: TableType<TData>;
    globalFilterPlaceholder?: string;
    facetedFilters?: Array<FacetConfig>;
    customFilters?: Array<CustomFilterConfig>;
    hideGlobalSearch?: boolean;
    extraButtons?: React.ReactNode;
    /** Controlled value for global search (when provided, search becomes controlled) */
    globalFilterValue?: string;
    /** Callback when global search changes (used with globalFilterValue for controlled mode) */
    onGlobalFilterChange?: (value: string) => void;
    /** When true, skip client-side filtering (use when server handles filtering) */
    serverSideGlobalFilter?: boolean;
    /** When true, show a loading indicator next to the search input */
    isLoading?: boolean;
    /** Use tighter, single-row controls on mobile screens. */
    mobileCompact?: boolean;
    /** Hide the column-visibility dropdown (for filter-only toolbars). */
    hideColumnsButton?: boolean;
    /** Render the Columns menu as a drag-to-reorder list instead of the dropdown. */
    enableColumnReorder?: boolean;
    onFilterChanged?: (event: ToolbarFilterChangedEvent) => void;
    onFiltersCleared?: (event: ToolbarFiltersClearedEvent) => void;
}) {
    const tableState = table.state;
    // Use controlled value if provided, otherwise fall back to table state
    const isControlled = globalFilterValue !== undefined;
    const globalFilter = isControlled
        ? globalFilterValue
        : ((tableState.globalFilter as string) ?? '');
    const columnFilters = React.useMemo(
        () => tableState.columnFilters ?? EMPTY_COLUMN_FILTERS,
        [tableState.columnFilters],
    );

    // Sync table's internal globalFilter with controlled value on mount/change.
    // This ensures localStorage-persisted filter state doesn't override the controlled value.
    // Under server-side search the table must hold no global filter at all: the
    // server already decided which rows match, so any term left in table state
    // (persisted from a client-side view of the same table) filters those rows
    // back out. A server-matched term that is not literal cell text — a
    // punctuated name, a match on a column the table does not render — then
    // shows an empty table.
    React.useEffect(() => {
        if (!isControlled) {
            return;
        }
        const nextGlobalFilter = serverSideGlobalFilter
            ? ''
            : (globalFilterValue ?? '');
        if (nextGlobalFilter !== tableState.globalFilter) {
            table.setGlobalFilter(nextGlobalFilter);
        }
    }, [
        isControlled,
        serverSideGlobalFilter,
        globalFilterValue,
        tableState.globalFilter,
        table,
    ]);

    const [pendingSelections, setPendingSelections] = React.useState<
        Map<string, { include: Set<string>; exclude: Set<string> }>
    >(new Map());

    const [facetSearchQueries, setFacetSearchQueries] = React.useState<
        Map<string, string>
    >(new Map());

    const facetModeDefaultsById = React.useMemo(
        () =>
            new Map(
                facetedFilters.map((facet) => [
                    facet.columnId,
                    {
                        includeMode: facet.defaultIncludeMode,
                        excludeMode: facet.defaultExcludeMode,
                    },
                ]),
            ),
        [facetedFilters],
    );

    const setFacetSelection = React.useCallback(
        (
            columnId: string,
            updater: (current: FacetSelectionState) => FacetSelectionState,
        ) =>
            applyFacetSelection(
                table,
                columnId,
                updater,
                facetModeDefaultsById.get(columnId),
            ),
        [facetModeDefaultsById, table],
    );

    const updateFacetSelection = React.useCallback(
        (
            columnId: string,
            updater: (current: FacetSelectionState) => FacetSelectionState,
        ) => {
            const column = table.getColumn(columnId);
            if (!column) {
                return;
            }
            const current = normalizeFacetSelection(column.getFilterValue());
            const nextSelection = updater(current);
            const include = dedupe(nextSelection.include);
            const exclude = dedupe(nextSelection.exclude);

            setPendingSelections((prev) => {
                const next = new Map(prev);
                next.set(columnId, {
                    include: new Set(include.map((v) => serialize(v))),
                    exclude: new Set(exclude.map((v) => serialize(v))),
                });
                return next;
            });

            React.startTransition(() => {
                setFacetSelection(columnId, () => ({
                    ...nextSelection,
                    include,
                    exclude,
                }));
            });
        },
        [setFacetSelection, table],
    );

    // The table instance is referentially stable, so data-derived facet
    // options must also recompute when the rows themselves change — without
    // the data dependency, facets computed before async rows arrive would
    // stay empty forever.
    const tableData = table.options.data;
    const facetContexts = React.useMemo(() => {
        void tableData;
        return facetedFilters
            .map((facet) => buildFacetContext(table, facet, columnFilters))
            .filter((ctx): ctx is FacetContext => ctx !== null);
    }, [table, facetedFilters, columnFilters, tableData]);

    const customFilterCount = React.useMemo(
        () =>
            customFilters.reduce(
                (sum, filter) => sum + filter.selectedCount,
                0,
            ),
        [customFilters],
    );

    const activeCustomFilters = React.useMemo(
        () =>
            customFilters.filter(
                (filter) =>
                    filter.selectedCount > 0 ||
                    (filter.activeFilterLabels?.length ?? 0) > 0,
            ),
        [customFilters],
    );

    const selectedCount = React.useMemo(
        () =>
            facetContexts.reduce(
                (sum, ctx) =>
                    sum +
                    ctx.selection.include.length +
                    ctx.selection.exclude.length,
                0,
            ) + customFilterCount,
        [facetContexts, customFilterCount],
    );

    const activeFacetEntries = React.useMemo(
        () => buildActiveFacetEntries(facetContexts),
        [facetContexts],
    );

    const toggleFacetValue = React.useCallback(
        (columnId: string, value: unknown, mode: 'include' | 'exclude') =>
            updateFacetSelection(columnId, (current) => {
                const valueSerialized = serialize(value);
                const withoutValue = (arr: Array<unknown>) =>
                    arr.filter((v) => serialize(v) !== valueSerialized);
                const hasValue = (arr: Array<unknown>) =>
                    arr.some((v) => serialize(v) === valueSerialized);

                if (mode === 'include') {
                    return {
                        include: hasValue(current.include)
                            ? withoutValue(current.include)
                            : [...withoutValue(current.include), value],
                        exclude: withoutValue(current.exclude),
                        includeMode: current.includeMode,
                        excludeMode: current.excludeMode,
                    };
                }

                return {
                    include: withoutValue(current.include),
                    exclude: hasValue(current.exclude)
                        ? withoutValue(current.exclude)
                        : [...withoutValue(current.exclude), value],
                    includeMode: current.includeMode,
                    excludeMode: current.excludeMode,
                };
            }),
        [updateFacetSelection],
    );

    React.useEffect(() => {
        setPendingSelections((prev) => (prev.size === 0 ? prev : new Map()));
    }, [columnFilters]);

    const clearFacet = React.useCallback(
        (columnId: string) =>
            updateFacetSelection(columnId, () => ({
                include: [],
                exclude: [],
            })),
        [updateFacetSelection],
    );

    const clearFacetValues = React.useCallback(
        (columnId: string, values: Array<unknown>) =>
            updateFacetSelection(columnId, (current) => {
                const valuesToRemove = new Set(values.map(serialize));
                return {
                    include: current.include.filter(
                        (v) => !valuesToRemove.has(serialize(v)),
                    ),
                    exclude: current.exclude.filter(
                        (v) => !valuesToRemove.has(serialize(v)),
                    ),
                    includeMode: current.includeMode,
                    excludeMode: current.excludeMode,
                };
            }),
        [updateFacetSelection],
    );

    const setFacetValuesType = React.useCallback(
        (
            columnId: string,
            values: Array<unknown>,
            type: ActiveFacetEntry['type'],
            mode?: 'any' | 'all',
        ) =>
            updateFacetSelection(columnId, (current) => {
                const valuesToSet = new Set(values.map(serialize));
                const withoutValues = (arr: Array<unknown>) =>
                    arr.filter((v) => !valuesToSet.has(serialize(v)));

                if (type === 'include') {
                    return {
                        include: dedupe([
                            ...withoutValues(current.include),
                            ...values,
                        ]),
                        exclude: withoutValues(current.exclude),
                        includeMode: mode ?? current.includeMode,
                        excludeMode: current.excludeMode,
                    };
                }

                return {
                    include: withoutValues(current.include),
                    exclude: dedupe([
                        ...withoutValues(current.exclude),
                        ...values,
                    ]),
                    includeMode: current.includeMode,
                    excludeMode: mode ?? current.excludeMode,
                };
            }),
        [updateFacetSelection],
    );

    const clearAll = React.useCallback(() => {
        activeCustomFilters.forEach((filter) => {
            filter.onClear?.();
        });
        onFiltersCleared?.({
            hadSearch: globalFilter.length > 0,
            clearedFacetCount: columnFilters.length,
            clearedCustomFilterCount: activeCustomFilters.length,
        });
        if (facetedFilters.length > 0) {
            setPendingSelections(() => {
                const next = new Map<
                    string,
                    { include: Set<string>; exclude: Set<string> }
                >();
                facetedFilters.forEach((facet) => {
                    next.set(facet.columnId, {
                        include: new Set(),
                        exclude: new Set(),
                    });
                });
                return next;
            });
        } else {
            setPendingSelections(new Map());
        }

        React.startTransition(() => {
            if (isControlled && onGlobalFilterChange) {
                onGlobalFilterChange('');
            } else {
                table.resetGlobalFilter();
            }
            table.resetColumnFilters();
        });
    }, [
        activeCustomFilters,
        columnFilters.length,
        facetedFilters,
        globalFilter.length,
        isControlled,
        onFiltersCleared,
        onGlobalFilterChange,
        table,
    ]);

    const allLeafColumns = table.getAllLeafColumns();
    const mobileCompactButtonClassName = mobileCompact
        ? 'max-md:h-9 max-md:w-9 max-md:px-0'
        : undefined;

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-2',
                mobileCompact && 'max-md:gap-1',
            )}
        >
            <div
                className={cn(
                    'flex w-full flex-wrap items-center gap-2',
                    mobileCompact &&
                        'max-md:scroll-px-2 max-md:flex-nowrap max-md:gap-1.5 max-md:overflow-x-auto max-md:overscroll-x-contain max-md:px-0.5 max-md:pt-1 max-md:pb-1 max-md:[-webkit-overflow-scrolling:touch] max-md:[&>*]:shrink-0',
                )}
            >
                {!hideGlobalSearch && (
                    <div
                        className={cn(
                            'relative w-full md:max-w-xl',
                            mobileCompact && 'max-md:min-w-0 max-md:flex-1',
                        )}
                    >
                        <Input
                            placeholder={globalFilterPlaceholder}
                            value={globalFilter}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (isControlled && onGlobalFilterChange) {
                                    onGlobalFilterChange(value);
                                }
                                // Sync table state for client-side filtering, or when clearing
                                // search to avoid stale filter when switching from server-side mode
                                if (!serverSideGlobalFilter || value === '') {
                                    table.setGlobalFilter(value);
                                }
                            }}
                            className={cn(
                                'w-full',
                                mobileCompact && 'max-md:h-9 max-md:text-sm',
                                globalFilter.length > 0 && 'pr-9',
                            )}
                        />
                        {isLoading ? (
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                            </div>
                        ) : (
                            globalFilter.length > 0 && (
                                <button
                                    type="button"
                                    aria-label="Clear search"
                                    onMouseDown={(event) =>
                                        event.preventDefault()
                                    }
                                    onClick={() => {
                                        if (
                                            isControlled &&
                                            onGlobalFilterChange
                                        ) {
                                            onGlobalFilterChange('');
                                        }
                                        // Always reset table state on clear,
                                        // matching the onChange path for value === ''.
                                        table.setGlobalFilter('');
                                    }}
                                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm outline-none focus-visible:ring-2"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )
                        )}
                    </div>
                )}
                {(facetContexts.length > 0 || customFilters.length > 0) && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                    'relative',
                                    mobileCompactButtonClassName,
                                )}
                                aria-label="Filter"
                            >
                                <ListFilter className="h-4 w-4 md:mr-2" />
                                <span className="hidden md:inline">Filter</span>
                                {selectedCount > 0 && (
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            'bg-primary text-primary-foreground ml-2 rounded-sm px-1.5 py-0.5 text-xs font-semibold',
                                            mobileCompact &&
                                                'max-md:absolute max-md:top-0 max-md:right-0 max-md:ml-0 max-md:h-4 max-md:min-w-4 max-md:rounded-full max-md:px-1 max-md:py-0 max-md:text-[10px] max-md:leading-4',
                                        )}
                                    >
                                        {selectedCount}
                                    </Badge>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
                            {customFilters.map((filter) => {
                                const IconComponent = filter.icon || Building;

                                return (
                                    <DropdownMenuSub
                                        key={filter.id}
                                        onOpenChange={(open) => {
                                            if (open && filter.onOpen) {
                                                filter.onOpen();
                                            }
                                        }}
                                    >
                                        <DropdownMenuSubTrigger>
                                            <IconComponent className="mr-2 h-4 w-4" />
                                            <span>{filter.title}</span>
                                            {filter.selectedCount > 0 && (
                                                <Badge
                                                    variant="secondary"
                                                    className="bg-primary text-primary-foreground ml-auto rounded-sm px-1.5 py-0.5 text-xs font-semibold"
                                                >
                                                    {filter.selectedCount}
                                                </Badge>
                                            )}
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="w-64">
                                            {filter.children}
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                );
                            })}
                            {facetContexts.map(
                                ({ facet, selection, options }) => {
                                    const IconComponent =
                                        facet.icon || Building;
                                    const filterType =
                                        facet.analyticsId ?? facet.columnId;
                                    const searchQuery =
                                        facetSearchQueries.get(
                                            facet.columnId,
                                        ) ?? '';
                                    const filteredOptions = searchQuery
                                        ? options.filter((opt) =>
                                              opt.label
                                                  .toLowerCase()
                                                  .includes(
                                                      searchQuery.toLowerCase(),
                                                  ),
                                          )
                                        : options;

                                    return (
                                        <DropdownMenuSub
                                            key={facet.columnId}
                                            onOpenChange={(open) => {
                                                if (!open) {
                                                    setFacetSearchQueries(
                                                        (prev) => {
                                                            const next =
                                                                new Map(prev);
                                                            next.delete(
                                                                facet.columnId,
                                                            );
                                                            return next;
                                                        },
                                                    );
                                                }
                                            }}
                                        >
                                            <DropdownMenuSubTrigger>
                                                <IconComponent className="mr-2 h-4 w-4" />
                                                <span>{facet.title}</span>
                                            </DropdownMenuSubTrigger>
                                            <DropdownMenuSubContent className="w-64">
                                                {options.length > 5 && (
                                                    <div className="border-b px-2 pb-2">
                                                        <Input
                                                            placeholder={`Search ${facet.title.toLowerCase()}...`}
                                                            value={searchQuery}
                                                            onChange={(e) => {
                                                                const value =
                                                                    e.target
                                                                        .value;
                                                                setFacetSearchQueries(
                                                                    (prev) => {
                                                                        const next =
                                                                            new Map(
                                                                                prev,
                                                                            );
                                                                        if (
                                                                            value
                                                                        ) {
                                                                            next.set(
                                                                                facet.columnId,
                                                                                value,
                                                                            );
                                                                        } else {
                                                                            next.delete(
                                                                                facet.columnId,
                                                                            );
                                                                        }
                                                                        return next;
                                                                    },
                                                                );
                                                            }}
                                                            className="h-8"
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            onKeyDown={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {filteredOptions.length > 0 ? (
                                                    <div className="max-h-64 overflow-y-auto">
                                                        {filteredOptions.map(
                                                            ({
                                                                key,
                                                                label,
                                                                value,
                                                                isIncluded,
                                                                isExcluded,
                                                            }) => {
                                                                const pending =
                                                                    pendingSelections.get(
                                                                        facet.columnId,
                                                                    );
                                                                const valueSerialized =
                                                                    serialize(
                                                                        value,
                                                                    );
                                                                const showIncluded =
                                                                    pending
                                                                        ? pending.include.has(
                                                                              valueSerialized,
                                                                          )
                                                                        : isIncluded;
                                                                const showExcluded =
                                                                    pending
                                                                        ? pending.exclude.has(
                                                                              valueSerialized,
                                                                          )
                                                                        : isExcluded;
                                                                return (
                                                                    <DropdownMenuItem
                                                                        key={
                                                                            key
                                                                        }
                                                                        className="flex items-center gap-2"
                                                                        onSelect={(
                                                                            event,
                                                                        ) =>
                                                                            event.preventDefault()
                                                                        }
                                                                    >
                                                                        <span className="min-w-0 flex-1 truncate text-sm">
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                        <span className="flex items-center gap-1">
                                                                            <Button
                                                                                type="button"
                                                                                size="icon"
                                                                                variant={
                                                                                    showIncluded
                                                                                        ? 'default'
                                                                                        : 'outline'
                                                                                }
                                                                                className="h-6 w-6"
                                                                                onPointerDown={(
                                                                                    event,
                                                                                ) =>
                                                                                    event.stopPropagation()
                                                                                }
                                                                                onClick={(
                                                                                    event,
                                                                                ) => {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    onFilterChanged?.(
                                                                                        {
                                                                                            filterType,
                                                                                            action: showIncluded
                                                                                                ? 'remove'
                                                                                                : 'include',
                                                                                            value: label,
                                                                                            filterMode:
                                                                                                'include',
                                                                                        },
                                                                                    );
                                                                                    toggleFacetValue(
                                                                                        facet.columnId,
                                                                                        value,
                                                                                        'include',
                                                                                    );
                                                                                }}
                                                                                aria-label={`Include ${label}`}
                                                                            >
                                                                                <CirclePlus
                                                                                    className={cn(
                                                                                        'h-4 w-4',
                                                                                        showIncluded
                                                                                            ? 'text-white'
                                                                                            : undefined,
                                                                                    )}
                                                                                />
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                size="icon"
                                                                                variant={
                                                                                    showExcluded
                                                                                        ? 'destructive'
                                                                                        : 'outline'
                                                                                }
                                                                                className="h-6 w-6"
                                                                                onPointerDown={(
                                                                                    event,
                                                                                ) =>
                                                                                    event.stopPropagation()
                                                                                }
                                                                                onClick={(
                                                                                    event,
                                                                                ) => {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    onFilterChanged?.(
                                                                                        {
                                                                                            filterType,
                                                                                            action: showExcluded
                                                                                                ? 'remove'
                                                                                                : 'exclude',
                                                                                            value: label,
                                                                                            filterMode:
                                                                                                'exclude',
                                                                                        },
                                                                                    );
                                                                                    toggleFacetValue(
                                                                                        facet.columnId,
                                                                                        value,
                                                                                        'exclude',
                                                                                    );
                                                                                }}
                                                                                aria-label={`Exclude ${label}`}
                                                                            >
                                                                                <CircleMinus
                                                                                    className={cn(
                                                                                        'h-4 w-4',
                                                                                        showExcluded
                                                                                            ? 'text-white'
                                                                                            : undefined,
                                                                                    )}
                                                                                />
                                                                            </Button>
                                                                        </span>
                                                                    </DropdownMenuItem>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                ) : (
                                                    <DropdownMenuItem
                                                        disabled={true}
                                                    >
                                                        {searchQuery
                                                            ? 'No matching options'
                                                            : 'No options available'}
                                                    </DropdownMenuItem>
                                                )}
                                                {(selection.include.length >
                                                    0 ||
                                                    selection.exclude.length >
                                                        0) && (
                                                    <React.Fragment>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => {
                                                                onFilterChanged?.(
                                                                    {
                                                                        filterType,
                                                                        action: 'clear',
                                                                    },
                                                                );
                                                                clearFacet(
                                                                    facet.columnId,
                                                                );
                                                            }}
                                                        >
                                                            Clear {facet.title}
                                                        </DropdownMenuItem>
                                                    </React.Fragment>
                                                )}
                                            </DropdownMenuSubContent>
                                        </DropdownMenuSub>
                                    );
                                },
                            )}
                            {columnFilters.length > 0 ||
                            globalFilter ||
                            activeCustomFilters.length > 0 ? (
                                <React.Fragment>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={clearAll}>
                                        Clear all
                                    </DropdownMenuItem>
                                </React.Fragment>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {!hideColumnsButton &&
                    (enableColumnReorder ? (
                        <ColumnReorderPopover
                            table={table}
                            buttonClassName={mobileCompactButtonClassName}
                        />
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild={true}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    aria-label="Toggle columns"
                                    className={mobileCompactButtonClassName}
                                >
                                    <Eye className="h-4 w-4 md:mr-2" />
                                    <span className="hidden md:inline">
                                        Columns
                                    </span>
                                    <ChevronDown className="ml-1 hidden h-4 w-4 md:inline-block" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {allLeafColumns.map((c) => (
                                    <DropdownMenuCheckboxItem
                                        key={c.id}
                                        className="capitalize"
                                        checked={c.getIsVisible()}
                                        onCheckedChange={(checked) =>
                                            c.toggleVisibility(!!checked)
                                        }
                                    >
                                        {getColumnDisplayName(c)}
                                    </DropdownMenuCheckboxItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ))}
                {extraButtons}
                {(columnFilters.length > 0 ||
                    globalFilter ||
                    activeCustomFilters.length > 0) && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        aria-label="Clear filters and search"
                        className={mobileCompactButtonClassName}
                    >
                        {mobileCompact && <X className="h-4 w-4 md:hidden" />}
                        <span
                            className={
                                mobileCompact ? 'hidden md:inline' : undefined
                            }
                        >
                            Clear
                        </span>
                    </Button>
                )}
            </div>
            {(activeFacetEntries.length > 0 ||
                customFilters.some(
                    (f) =>
                        f.activeFilterLabels && f.activeFilterLabels.length > 0,
                )) && (
                <div
                    className={cn(
                        'flex flex-wrap items-center gap-2',
                        mobileCompact &&
                            'max-md:scroll-px-2 max-md:flex-nowrap max-md:overflow-x-auto max-md:overscroll-x-contain max-md:px-0.5 max-md:pt-0.5 max-md:pb-1 max-md:[-webkit-overflow-scrolling:touch] max-md:[&>*]:shrink-0',
                    )}
                >
                    {customFilters
                        .filter(
                            (f) =>
                                f.activeFilterLabels &&
                                f.activeFilterLabels.length > 0,
                        )
                        .map((filter) => (
                            <CustomFilterChipInner
                                key={filter.id}
                                filter={filter}
                            />
                        ))}
                    {activeFacetEntries.map((entry) => {
                        const hasOptions = entry.options.length > 0;
                        const valueLabel =
                            entry.labels.length > 0
                                ? entry.labels.join(', ')
                                : '—';
                        const includeMode =
                            entry.includeMode ??
                            (entry.values.length > 1 ? 'any' : undefined);
                        const excludeMode =
                            entry.excludeMode ??
                            (entry.values.length > 1 ? 'any' : undefined);
                        const operatorLabel = entry.isArray
                            ? entry.type === 'include'
                                ? includeMode === 'all'
                                    ? 'includes all of'
                                    : 'includes any of'
                                : excludeMode === 'all'
                                  ? 'excludes all of'
                                  : 'excludes any of'
                            : entry.type === 'include'
                              ? 'is'
                              : 'is not';
                        const handleOperatorSelect = ({
                            type,
                            mode,
                            operator,
                            isActive,
                        }: {
                            readonly type: ActiveFacetEntry['type'];
                            readonly mode?: 'any' | 'all';
                            readonly operator:
                                | 'is'
                                | 'is_not'
                                | 'includes_any'
                                | 'includes_all'
                                | 'excludes_any'
                                | 'excludes_all';
                            readonly isActive: boolean;
                        }) => {
                            if (isActive) {
                                return;
                            }

                            onFilterChanged?.({
                                filterType: entry.analyticsId,
                                action: 'change_operator',
                                filterMode: type,
                                operator,
                            });
                            setFacetValuesType(
                                entry.columnId,
                                entry.values,
                                type,
                                mode,
                            );
                        };
                        const operatorOptions: Array<{
                            label: string;
                            isActive: boolean;
                            type: ActiveFacetEntry['type'];
                            mode?: 'any' | 'all';
                            operator:
                                | 'is'
                                | 'is_not'
                                | 'includes_any'
                                | 'includes_all'
                                | 'excludes_any'
                                | 'excludes_all';
                        }> = [];

                        if (entry.isArray) {
                            operatorOptions.push({
                                label: 'includes any of',
                                isActive:
                                    entry.type === 'include' &&
                                    includeMode !== 'all',
                                type: 'include',
                                mode: 'any',
                                operator: 'includes_any',
                            });
                            operatorOptions.push({
                                label: 'includes all of',
                                isActive:
                                    entry.type === 'include' &&
                                    includeMode === 'all',
                                type: 'include',
                                mode: 'all',
                                operator: 'includes_all',
                            });
                            operatorOptions.push({
                                label: 'excludes any of',
                                isActive:
                                    entry.type === 'exclude' &&
                                    excludeMode !== 'all',
                                type: 'exclude',
                                mode: 'any',
                                operator: 'excludes_any',
                            });
                            operatorOptions.push({
                                label: 'excludes all of',
                                isActive:
                                    entry.type === 'exclude' &&
                                    excludeMode === 'all',
                                type: 'exclude',
                                mode: 'all',
                                operator: 'excludes_all',
                            });
                        } else {
                            operatorOptions.push({
                                label: 'is',
                                isActive: entry.type === 'include',
                                type: 'include',
                                operator: 'is',
                            });
                            operatorOptions.push({
                                label: 'is not',
                                isActive: entry.type === 'exclude',
                                type: 'exclude',
                                operator: 'is_not',
                            });
                        }

                        return (
                            <div
                                key={entry.key}
                                className="border-border bg-muted/60 flex items-center gap-1 rounded-full border py-1 pr-1 pl-3"
                            >
                                <span className="text-xs font-medium">
                                    {entry.facetTitle}
                                </span>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild={true}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className={cn(
                                                'h-7 items-center gap-1 rounded-full px-3 text-xs',
                                                entry.type === 'exclude'
                                                    ? 'border-destructive text-destructive hover:bg-destructive/10'
                                                    : 'border-blue-500 text-blue-600 hover:bg-blue-50',
                                            )}
                                        >
                                            <span className="truncate">
                                                {operatorLabel}
                                            </span>
                                            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        {operatorOptions.map((option) => (
                                            <DropdownMenuItem
                                                key={option.label}
                                                data-active={option.isActive}
                                                className={cn(
                                                    'text-xs',
                                                    option.isActive
                                                        ? 'font-semibold'
                                                        : undefined,
                                                )}
                                                onSelect={(event) => {
                                                    event.preventDefault();
                                                    handleOperatorSelect(
                                                        option,
                                                    );
                                                }}
                                            >
                                                {option.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                {hasOptions ? (
                                    <DropdownMenu modal={false}>
                                        <DropdownMenuTrigger asChild={true}>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex h-7 max-w-[16rem] min-w-[6rem] items-center gap-1 truncate rounded-full px-3 text-xs"
                                                title={valueLabel}
                                            >
                                                <span className="flex-1 truncate text-left">
                                                    {valueLabel}
                                                </span>
                                                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            align="start"
                                            className="max-h-72 w-64"
                                        >
                                            <DropdownMenuLabel>
                                                Select values
                                            </DropdownMenuLabel>
                                            {entry.options.map((option) => {
                                                const pending =
                                                    pendingSelections.get(
                                                        entry.columnId,
                                                    );
                                                const isSelected = pending
                                                    ? entry.type === 'include'
                                                        ? pending.include.has(
                                                              option.serialized,
                                                          )
                                                        : pending.exclude.has(
                                                              option.serialized,
                                                          )
                                                    : entry.serializedValues.includes(
                                                          option.serialized,
                                                      );
                                                return (
                                                    <DropdownMenuCheckboxItem
                                                        key={option.key}
                                                        checked={isSelected}
                                                        onCheckedChange={(
                                                            checked,
                                                        ) => {
                                                            onFilterChanged?.({
                                                                filterType:
                                                                    entry.analyticsId,
                                                                action: checked
                                                                    ? entry.type
                                                                    : 'remove',
                                                                value: option.label,
                                                                filterMode:
                                                                    entry.type,
                                                            });
                                                            updateFacetSelection(
                                                                entry.columnId,
                                                                (current) => {
                                                                    const valueSerialized =
                                                                        serialize(
                                                                            option.value,
                                                                        );
                                                                    const withoutValue =
                                                                        (
                                                                            arr: Array<unknown>,
                                                                        ) =>
                                                                            arr.filter(
                                                                                (
                                                                                    v,
                                                                                ) =>
                                                                                    serialize(
                                                                                        v,
                                                                                    ) !==
                                                                                    valueSerialized,
                                                                            );

                                                                    if (
                                                                        entry.type ===
                                                                        'include'
                                                                    ) {
                                                                        return {
                                                                            include:
                                                                                checked
                                                                                    ? dedupe(
                                                                                          [
                                                                                              ...current.include,
                                                                                              option.value,
                                                                                          ],
                                                                                      )
                                                                                    : withoutValue(
                                                                                          current.include,
                                                                                      ),
                                                                            exclude:
                                                                                withoutValue(
                                                                                    current.exclude,
                                                                                ),
                                                                            includeMode:
                                                                                current.includeMode,
                                                                        };
                                                                    }

                                                                    return {
                                                                        include:
                                                                            withoutValue(
                                                                                current.include,
                                                                            ),
                                                                        exclude:
                                                                            checked
                                                                                ? dedupe(
                                                                                      [
                                                                                          ...current.exclude,
                                                                                          option.value,
                                                                                      ],
                                                                                  )
                                                                                : withoutValue(
                                                                                      current.exclude,
                                                                                  ),
                                                                        includeMode:
                                                                            current.includeMode,
                                                                    };
                                                                },
                                                            );
                                                        }}
                                                        onSelect={(event) =>
                                                            event.preventDefault()
                                                        }
                                                    >
                                                        {option.label}
                                                    </DropdownMenuCheckboxItem>
                                                );
                                            })}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                ) : (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={true}
                                        className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                    >
                                        {valueLabel}
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 rounded-full"
                                    onClick={() => {
                                        onFilterChanged?.({
                                            filterType: entry.analyticsId,
                                            action: 'clear',
                                        });
                                        clearFacetValues(
                                            entry.columnId,
                                            entry.values,
                                        );
                                    }}
                                    aria-label={`Clear ${entry.facetTitle} filter for ${valueLabel}`}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
